import { useState } from 'react';

interface InvestmentTypeOption {
  key: string;
  label: string;
}

interface Props {
  assessmentId: string;
  options: InvestmentTypeOption[];
  onConfirm: (key: string, label: string) => void;
  onClose: () => void;
  isSubmitting: boolean;
}

export function InvestmentTypeModal({ options, onConfirm, onClose, isSubmitting }: Props) {
  const [selected, setSelected] = useState<string>('');

  const selectedOption = options.find((o) => o.key === selected);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--s4)',
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          // Responsive sizing (UI-28): scale with viewport but cap so it
          // doesn't dominate desktops; cap height so the options list
          // scrolls if there are many catalog entries.
          width: 'min(560px, 92vw)',
          maxHeight: 'min(90vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header">
          <h2 className="t-h3">Confirm Investment Type</h2>
        </div>
        <div style={{ padding: 'var(--s3) var(--s4)', overflowY: 'auto', flex: 1 }}>
          <p className="t-sm t-muted" style={{ marginBottom: 'var(--s3)' }}>
            The investment type could not be determined from the uploaded documents.
            Please select the correct type to complete the analysis.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s2)' }}>
            {options.map((opt) => (
              <label
                key={opt.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--s2)',
                  padding: 'var(--s2) var(--s3)',
                  border: `2px solid ${selected === opt.key ? 'var(--color-dd)' : 'var(--border)'}`,
                  borderRadius: 'var(--r)',
                  cursor: 'pointer',
                  background: selected === opt.key ? 'rgba(var(--color-dd-rgb), 0.06)' : undefined,
                  transition: 'border-color 0.15s',
                }}
              >
                <input
                  type="radio"
                  name="investment_type"
                  value={opt.key}
                  checked={selected === opt.key}
                  onChange={() => setSelected(opt.key)}
                  style={{ accentColor: 'var(--color-dd)' }}
                />
                <span className="t-sm" style={{ fontWeight: selected === opt.key ? 600 : undefined }}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div style={{ padding: 'var(--s3) var(--s4)', borderTop: '1px solid var(--border)', display: 'flex', gap: 'var(--s2)', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={isSubmitting} type="button">
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!selected || isSubmitting}
            onClick={() => selectedOption && onConfirm(selectedOption.key, selectedOption.label)}
            type="button"
          >
            {isSubmitting ? (
              <><span className="spinner" style={{ width: 12, height: 12 }} /> Re-running analysis…</>
            ) : 'Confirm & Re-run Analysis'}
          </button>
        </div>
      </div>
    </div>
  );
}
